import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { WhatsAppShareButton } from "../components/WhatsAppShareButton";
import { FeaturedBadge } from "../components/Featured";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Building2, Calendar, ArrowLeft, Send, CheckCircle, AlertCircle, Loader2, Lock, CreditCard, Sparkles, Phone, Mail, MessageSquare, FileText, User, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappatura tipi contratto
const CONTRACT_TYPE_LABELS = {
  tempo_indeterminato: "Tempo indeterminato",
  tempo_determinato: "Tempo determinato",
  apprendistato: "Apprendistato",
  stage: "Stage/Tirocinio",
  contratto_progetto: "Contratto a progetto",
  lavoro_temporaneo: "Lavoro temporaneo",
  freelance: "Freelance",
  part_time: "Part-time",
  full_time: "Full-time",
  stagionale: "Stagionale",
  a_chiamata: "A chiamata"
};

// Mappatura tipi inserzionista
const ADVERTISER_TYPE_LABELS = {
  company: "Azienda",
  agency: "Agenzia di lavoro",
  private: "Annuncio privato"
};

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState("");
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jobRes = await axios.get(`${API}/jobs/${jobId}`);
        setJob(jobRes.data);
        
        if (user && user.user_type === "candidate") {
          try {
            const appsRes = await axios.get(`${API}/applications`);
            const applied = appsRes.data.some(app => app.job_id === jobId);
            setHasApplied(applied);
          } catch (e) {
            console.error("Failed to check application status:", e);
          }
        }
      } catch (error) {
        toast.error("Annuncio non trovato");
        navigate("/jobs");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [jobId, user, navigate]);

  // SEO: Add JobPosting JSON-LD schema
  useEffect(() => {
    if (!job) return;

    const employmentTypeMap = {
      tempo_indeterminato: "FULL_TIME",
      tempo_determinato: "TEMPORARY",
      apprendistato: "INTERN",
      stage: "INTERN",
      part_time: "PART_TIME",
      full_time: "FULL_TIME",
      freelance: "CONTRACTOR",
      stagionale: "SEASONAL",
      a_chiamata: "PER_DIEM"
    };

    const employmentTypes = (job.contract_types || [])
      .map(ct => employmentTypeMap[ct])
      .filter(Boolean);

    const jobPostingSchema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": job.title,
      "description": job.description,
      "datePosted": job.created_at ? job.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.company_name || "Azienda",
        "sameAs": "https://www.lavoroticino.ch"
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": job.location || "Ticino",
          "addressRegion": "Ticino",
          "addressCountry": "CH"
        }
      },
      "employmentType": employmentTypes.length > 0 ? employmentTypes : ["FULL_TIME"]
    };

    // Add industry if available
    if (job.settore) {
      jobPostingSchema.industry = job.settore;
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jobPostingSchema);
    script.id = 'job-posting-schema';
    
    // Remove existing if present
    const existing = document.getElementById('job-posting-schema');
    if (existing) existing.remove();
    
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('job-posting-schema');
      if (el) el.remove();
    };
  }, [job]);

  const handleApplyClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (user.user_type !== "candidate") {
      toast.error("Solo i candidati possono candidarsi");
      return;
    }

    // Candidatura gratuita - apri direttamente il modal
    setShowApplyModal(true);
  };

  const handleApply = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (user.user_type !== "candidate") {
      toast.error("Solo i candidati possono candidarsi");
      return;
    }

    // Verifica profilo completo
    if (!user.comune_residenza) {
      toast.error("Completa il tuo profilo prima di candidarti");
      navigate("/dashboard/candidate");
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/applications`, {
        job_id: jobId,
        message: applicationMessage || null
      });
      toast.success("Candidatura inviata! L'azienda riceverà i tuoi dati.");
      setHasApplied(true);
      setShowApplyModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setApplying(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-CH", { day: "numeric", month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex justify-center py-24"><div className="spinner"></div></div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="job-detail-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/jobs" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Torna agli annunci
        </Link>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {job.image_url ? (
            <img
              src={job.image_url.startsWith('http') ? job.image_url : `${process.env.REACT_APP_BACKEND_URL}${job.image_url}`}
              alt={job.title}
              className="w-full h-56 sm:h-72 object-cover"
              data-testid="job-detail-image"
            />
          ) : (
            <div className="w-full h-40 sm:h-48 bg-slate-50 border-b border-slate-100 flex items-center justify-center" data-testid="job-detail-logo">
              <img src="/logo-transparent.png" alt="LavoroTicino.ch" className="h-24 sm:h-28 object-contain" />
            </div>
          )}
          <div className="p-6 sm:p-8 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Badge className={job.location === "Remote" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                {job.location === "Remote" ? "Remoto" : job.location}
              </Badge>
              {job.advertiser_type === "private" && (
                <Badge className="bg-amber-100 text-amber-700">
                  <User className="w-3 h-3 mr-1" /> Annuncio privato
                </Badge>
              )}
              {job.contract_types && job.contract_types.length > 0 && (
                job.contract_types.map(ct => (
                  <Badge key={ct} variant="outline" className="border-slate-300">
                    {CONTRACT_TYPE_LABELS[ct] || ct}
                  </Badge>
                ))
              )}
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Pubblicato il {formatDate(job.created_at)}
              </span>
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{job.title}</h1>
            <div className="mb-4 flex items-center gap-3 flex-wrap"><FeaturedBadge item={job} /><WhatsAppShareButton kind="job" item={job} /></div>
            
            <div className="flex flex-wrap gap-4 text-slate-600">
              <div className="flex items-center gap-2">
                {job.advertiser_type === "private" ? (
                  <User className="w-5 h-5 text-[#004E64]" />
                ) : (
                  <Building2 className="w-5 h-5 text-[#004E64]" />
                )}
                <span className="font-medium">
                  {job.advertiser_type === "private" 
                    ? `Pubblicato da: ${job.private_first_name || job.company_name}` 
                    : job.company_name}
                </span>
                {job.advertiser_type !== "private" && (
                  <span className="text-sm text-slate-500">({ADVERTISER_TYPE_LABELS[job.advertiser_type] || "Azienda"})</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#DA291C]" />
                <span>{job.location}</span>
              </div>
            </div>
          </div>

          {/* Tipo di contratto e Settore */}
          {((job.contract_types && job.contract_types.length > 0) || job.sector) && (
            <div className="px-6 sm:px-8 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex flex-wrap gap-4">
                {job.sector && (
                  <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#004E64]" />
                    <span className="font-medium text-slate-700">Settore:</span>
                    <span className="text-slate-600">{job.sector}</span>
                  </div>
                )}
                {job.contract_types && job.contract_types.length > 0 && (
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#004E64]" />
                    <span className="font-medium text-slate-700">Tipo di contratto:</span>
                    <span className="text-slate-600">
                      {job.contract_types.map(ct => CONTRACT_TYPE_LABELS[ct] || ct).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Descrizione</h2>
            <p className="text-slate-600 whitespace-pre-wrap">{job.description}</p>
          </div>

          {/* Informazioni di contatto - basate sulle preferenze */}
          {job.contact_preferences && job.contact_preferences.length > 0 && (
            <div className="px-6 sm:px-8 py-5 border-t border-slate-100 bg-blue-50">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#004E64]" />
                Modalità di contatto
              </h3>
              <div className="space-y-2">
                {job.contact_preferences.includes("phone") && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Phone className="w-4 h-4 text-emerald-600" />
                    <span>Telefono: </span>
                    <a href={`tel:${job.advertiser_type === "private" ? job.private_phone : job.company_phone || ""}`} className="text-[#004E64] font-medium hover:underline">
                      {job.advertiser_type === "private" ? job.private_phone : (job.company_phone || "Disponibile")}
                    </a>
                  </div>
                )}
                {job.contact_preferences.includes("email") && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    <span>Email: </span>
                    <a href={`mailto:${job.advertiser_type === "private" ? job.private_email : job.company_email}`} className="text-[#004E64] font-medium hover:underline">
                      {job.advertiser_type === "private" ? job.private_email : job.company_email}
                    </a>
                  </div>
                )}
                {job.contact_preferences.includes("internal") && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <MessageSquare className="w-4 h-4 text-emerald-600" />
                    <span>Messaggistica interna del portale</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="p-6 sm:p-8 bg-slate-50 border-t border-slate-100">
            {hasApplied ? (
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Hai già inviato la tua candidatura</span>
              </div>
            ) : user && user.user_type !== "candidate" ? (
              <div className="flex items-center gap-2 text-slate-500">
                <AlertCircle className="w-5 h-5" />
                <span>Le aziende non possono candidarsi</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleApplyClick}
                  className="bg-[#DA291C] hover:bg-[#B91C1C] text-white h-12 px-8 text-base font-semibold"
                  data-testid="apply-button"
                >
                  {user && !user.subscription_active ? (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Candidati Ora
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Candidati Ora
                    </>
                  )}
                </Button>
                {user && !user.subscription_active && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <Lock className="w-4 h-4" />
                    Sblocca con abbonamento
                  </p>
                )}
              </div>
            )}
            
            <p className="text-xs text-slate-400 mt-4">
              La candidatura avviene tramite il portale. L'azienda riceverà: Nome, Comune, Bio ed Esperienze.
            </p>
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Candidati per questa posizione</DialogTitle>
            <DialogDescription>
              <strong>{job.title}</strong> presso {job.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <strong>Dati inviati:</strong> Nome ({user?.name}), Comune ({user?.comune_residenza || "Non specificato"}), Bio ed Esperienze dal tuo profilo.
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Messaggio aggiuntivo (opzionale)
              </label>
              <Textarea
                placeholder="Scrivi un messaggio per l'azienda..."
                value={applicationMessage}
                onChange={(e) => setApplicationMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyModal(false)}>Annulla</Button>
            <Button onClick={handleApply} disabled={applying} className="bg-[#DA291C] hover:bg-[#B91C1C] text-white">
              {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</> : <><Send className="w-4 h-4 mr-2" /> Invia candidatura</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paywall Modal */}
      <Dialog open={showPaywallModal} onOpenChange={setShowPaywallModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-emerald-600" />
            </div>
            <DialogTitle className="text-center text-xl">Sblocca le candidature</DialogTitle>
            <DialogDescription className="text-center">
              Con l'abbonamento annuale puoi candidarti a tutti gli annunci di lavoro.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Annual Badge - Primary Focus */}
            <div className="bg-emerald-700 text-white text-lg font-bold px-5 py-3 rounded-xl text-center mb-4">
              ABBONAMENTO ANNUALE
            </div>
            
            {/* Duration Highlight */}
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center mb-4">
              <p className="text-emerald-800 font-bold text-xl">Validità 365 giorni</p>
              <p className="text-emerald-600 text-sm mt-1">Meno di 2 CHF al mese</p>
            </div>
            
            {/* Price - Secondary */}
            <div className="text-center mb-4">
              <span className="text-2xl font-semibold text-slate-500">24 CHF</span>
              <span className="text-sm text-slate-400 ml-1">/ anno</span>
            </div>
            
            {/* Promo Code */}
            <p className="text-center text-sm text-amber-600 font-medium bg-amber-50 py-2 px-3 rounded-lg mb-4">
              Usa codice LANCIO50 per -50%
            </p>

            {/* Benefits */}
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Candidature illimitate
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Contatta le aziende direttamente
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Profilo visibile ai recruiter
              </li>
            </ul>
          </div>

          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button 
              onClick={() => navigate("/subscription")} 
              className="w-full h-12 bg-[#DA291C] hover:bg-[#B91C1C] text-white font-semibold"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Attiva abbonamento
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setShowPaywallModal(false)}
              className="w-full text-slate-500"
            >
              Continua a sfogliare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
